import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import logger from '../utils/logger';

const STORAGE_KEY_PREFIX = 'visibeen_default_profile';

const isBrowser = () => typeof window !== 'undefined';

// Get user ID from localStorage for user-specific storage
const getCurrentUserId = () => {
  if (!isBrowser()) return 'anonymous';
  try {
    const userDataStr = localStorage.getItem('userData');
    if (userDataStr) {
      const userData = JSON.parse(userDataStr);
      return userData.id || userData.user_id || userData.customer_id || 'anonymous';
    }
  } catch (e) {
    // Ignore parse errors
  }
  return 'anonymous';
};

// Generate user-specific storage key
const getStorageKey = () => {
  const userId = getCurrentUserId();
  return `${STORAGE_KEY_PREFIX}_${userId}`;
};

const isAdminImpersonationActive = () => {
  if (!isBrowser()) return false;
  try {
    const accessData = window.sessionStorage?.getItem('adminAccessData');
    const viewingId = window.sessionStorage?.getItem('adminViewingUserId');
    return Boolean(accessData || viewingId);
  } catch (error) {
    logger.warn('[DefaultProfileContext] Failed to detect admin view:', error?.message || error);
    return false;
  }
};

const readProfileFromStorage = () => {
  if (!isBrowser()) return null;
  const storageKey = getStorageKey();
  const storages = [];
  if (isAdminImpersonationActive()) {
    storages.push(window.sessionStorage);
  }
  storages.push(window.localStorage);

  for (const store of storages) {
    try {
      const raw = store?.getItem?.(storageKey);
      if (raw) return JSON.parse(raw);
    } catch (error) {
      logger.warn('[DefaultProfileContext] Failed to parse stored profile:', error?.message || error);
    }
  }
  return null;
};

const persistProfile = (profile) => {
  if (!isBrowser()) return;
  const storageKey = getStorageKey();
  const adminMode = isAdminImpersonationActive();
  const primaryStore = adminMode ? window.sessionStorage : window.localStorage;
  const secondaryStore = adminMode ? window.localStorage : window.sessionStorage;

  try {
    if (profile) {
      primaryStore?.setItem(storageKey, JSON.stringify(profile));
    } else {
      primaryStore?.removeItem(storageKey);
    }
  } catch (error) {
    logger.warn('[DefaultProfileContext] Failed to persist profile:', error?.message || error);
  }

  try {
    secondaryStore?.removeItem(storageKey);
  } catch (_) {
    // ignore cleanup failures
  }
};

const DefaultProfileContext = createContext();

export const useDefaultProfile = () => {
  const context = useContext(DefaultProfileContext);
  if (!context) {
    throw new Error('useDefaultProfile must be used within DefaultProfileProvider');
  }
  return context;
};

export const DefaultProfileProvider = ({ children }) => {
  const [defaultProfile, setDefaultProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const syncFromStorage = () => {
    const storedProfile = readProfileFromStorage();
    setDefaultProfile(storedProfile);
    setIsLoading(false);
    if (storedProfile) {
      logger.log('[DefaultProfileContext] Loaded default profile from storage:', {
        id: storedProfile.id,
        name: storedProfile.name,
        locationId: storedProfile.locationId
      });
    } else {
      logger.log('[DefaultProfileContext] No default profile found in storage');
    }
  };

  useEffect(() => {
    syncFromStorage();
  }, []);

  useEffect(() => {
    const handleProfileChange = (event) => {
      if (event?.detail !== undefined) {
        setDefaultProfile(event.detail);
        setIsLoading(false);
        return;
      }
      syncFromStorage();
    };

    const handleStorage = (event) => {
      if (!event || !event.key || !event.key.startsWith(STORAGE_KEY_PREFIX)) return;
      syncFromStorage();
    };

    window.addEventListener('defaultProfileChanged', handleProfileChange);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('defaultProfileChanged', handleProfileChange);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const updateDefaultProfile = useCallback((profile) => {
    try {
      persistProfile(profile);
      setDefaultProfile(profile);
      setIsLoading(false);

      logger.log(
        profile
          ? '[DefaultProfileContext] Default profile updated'
          : '[DefaultProfileContext] Default profile cleared'
      );

      window.dispatchEvent(
        new CustomEvent('defaultProfileChanged', {
          detail: profile ?? null
        })
      );
    } catch (error) {
      logger.error('[DefaultProfileContext] Error updating default profile:', error);
    }
  }, []);

  const clearDefaultProfile = useCallback(() => {
    updateDefaultProfile(null);
  }, [updateDefaultProfile]);

  const value = useMemo(() => ({
    defaultProfile,
    setDefaultProfile: updateDefaultProfile,
    clearDefaultProfile,
    isLoading
  }), [defaultProfile, updateDefaultProfile, clearDefaultProfile, isLoading]);

  return (
    <DefaultProfileContext.Provider value={value}>
      {children}
    </DefaultProfileContext.Provider>
  );
};

export default DefaultProfileContext;
